import { FC, useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Alert, Box, Button, Chip, Typography } from "@mui/material"
import { Block } from "@mui/icons-material"
import { GridColDef, GridRowParams } from "@mui/x-data-grid-pro"
import { DataGrid, Modal, SnackbarAlert } from "@/core/components"
import { useUrlPagination } from "@/core/hooks"
import { formatDateAndTime, getApiErrorMessage } from "@/core/utils"
import { GuestBot } from "../../interfaces"
import { useCancelMailingMutation, useMailingsListQuery } from "../../store"
import { useGuestBotWriteAccess } from "../../hooks"
import { TooltipAction } from "../common"
import {
  MAILING_STATUS_COLOR,
  MAILING_STATUS_LABEL,
} from "../../utils/chatStatus"

export const MailingsTable: FC = () => {
  const canWrite = useGuestBotWriteAccess()
  const navigate = useNavigate()
  const { page, pageSize, paginationModel, setPaginationModel } =
    useUrlPagination(50)
  const { data, isFetching, isError, error, refetch } = useMailingsListQuery({
    queryParams: { limit: pageSize, offset: page * pageSize },
  })
  const [cancelMailing, { isLoading: isCancelling }] =
    useCancelMailingMutation()
  const [toCancel, setToCancel] = useState<GuestBot.Mailing | null>(null)
  const [snackbar, setSnackbar] = useState<{
    variant: "success" | "error"
    message: string
  } | null>(null)

  const columns: GridColDef<GuestBot.Mailing>[] = [
    {
      field: "createdAt",
      headerName: "Создана",
      flex: 2,
      valueGetter: (params) => formatDateAndTime(params.row.createdAt),
    },
    {
      field: "botUsername",
      headerName: "Бот",
      flex: 1.5,
      valueGetter: (params) => params.row.botUsername ?? "—",
    },
    { field: "templateName", headerName: "Шаблон", flex: 2 },
    {
      field: "chats",
      headerName: "Чаты",
      flex: 2,
      // pending is optional per delta §4.3 (F3) — ?? 0 everywhere.
      valueGetter: (params) => {
        const s = params.row.chatsSummary
        return `Отправлено ${s.sent ?? 0} · Удалено ${s.deleted ?? 0} · Ошибки ${s.failed ?? 0} · Всего ${s.total ?? 0}`
      },
    },
    {
      field: "status",
      headerName: "Статус",
      flex: 1.5,
      renderCell: (params) => (
        <Chip
          size="small"
          label={MAILING_STATUS_LABEL[params.row.status]}
          color={MAILING_STATUS_COLOR[params.row.status]}
        />
      ),
    },
    { field: "lastError", headerName: "Ошибка", flex: 2 },
  ]

  const rowActions = useMemo(() => {
    const actions: ((
      params: GridRowParams<GuestBot.Mailing>,
    ) => React.ReactElement)[] = []
    if (canWrite) {
      actions.push((params) =>
        params.row.status === "draft" ? (
          <TooltipAction
            icon={<Block />}
            label="Отменить"
            onClick={() => setToCancel(params.row)}
          />
        ) : (
          <></>
        ),
      )
    }
    return actions
  }, [canWrite])

  const handleCancel = useCallback(async () => {
    if (!toCancel) return
    try {
      await cancelMailing({
        pathParams: { id: toCancel.botId, mailingId: toCancel.id },
      }).unwrap()
      setSnackbar({ variant: "success", message: "Рассылка отменена" })
    } catch (e) {
      setSnackbar({ variant: "error", message: getApiErrorMessage(e) })
    } finally {
      setToCancel(null)
    }
  }, [toCancel, cancelMailing])

  return (
    <>
      {isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Повторить
            </Button>
          }
        >
          {getApiErrorMessage(error)}
        </Alert>
      ) : !isFetching && (data?.total ?? 0) === 0 ? (
        <Typography>Рассылок пока нет.</Typography>
      ) : (
        <Box height="70vh">
          <DataGrid<GuestBot.Mailing>
            columns={columns}
            rows={data?.items ?? []}
            getRowId={(row) => row.id}
            loading={isFetching}
            paginationMode="server"
            rowCount={data?.total ?? 0}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pagination
            onRowClick={(params) =>
              navigate(
                `/telegram/guest-bots/mailings/${params.row.botId}/${params.row.id}`,
              )
            }
            rowActions={rowActions}
          />
        </Box>
      )}

      {toCancel && (
        <Modal
          disableBackdropClose
          title="Отменить рассылку?"
          onClose={() => setToCancel(null)}
          element={
            <Box>
              <Typography>
                Ожидающие чаты будут отменены. Уже отправленные сообщения не
                изменятся.
              </Typography>
              <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={() => setToCancel(null)}>Не отменять</Button>
                <Button
                  color="error"
                  variant="contained"
                  disabled={isCancelling}
                  onClick={handleCancel}
                >
                  Отменить рассылку
                </Button>
              </Box>
            </Box>
          }
        />
      )}
      <SnackbarAlert
        isOpen={!!snackbar}
        variant={snackbar?.variant}
        message={snackbar?.message ?? ""}
        onClose={() => setSnackbar(null)}
      />
    </>
  )
}
